"use client";

import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Button } from "./ui/button";
import { Link } from "@/i18n/routing";
import AccessButton from "./access-button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { AnimatedTabs } from "./ui/animated-tabs";
import { cn } from "@/lib/utils";
import {
  ChartPie,
  Network,
  ChartColumnIncreasing,
  NotepadText,
  Handshake,
  PieChart,
} from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { useUserStore } from "@/store/store";
import { TrainerIcon, ChartIcon, DartIcon } from "./icons";
import { GymReportIcon } from "./icons/gym-report";
import { SaladIcon } from "./icons/salad";

const tabValues = [
  "client_management",
  "workout_nutrition_planning",
  "progress",
  "marketing_tools",
  "team_collab",
];

const tabValuesClient = [
  "coaching",
  "workout",
  "nutrition",
  "progress",
  "goals",
];

// const icons = [
//   <ChartPie size={48} />,
//   <Network size={48} />,
//   <ChartColumnIncreasing size={48} />,
//   <NotepadText size={48} />,
//   <Handshake size={48} />,
// ];

const TabsSection = () => {
  const locale = useLocale();
  const t = useTranslations("home.tabs");
  const { role } = useUserStore();
  
  // Explicitly type the role to fix TypeScript error
  const currentRole = role as "trainer" | "client";
    // State to track current tab value
  const [currentTab, setCurrentTab] = useState<string>("");

  // Helper functions to get image sources based on role
  const getClientImageSrc = (tabValue: string, index: number) => {
    switch (tabValue) {
      case "coaching":
        return `/client/coaching/coaching-${index + 1}.webp`;
      case "workout":
        return `/client/workout-tracking/workout-tracking-${index + 1}.webp`;
      case "nutrition":
        return `/client/nutrition/home_tab.png`;
      case "progress":
        return `/client/workout-tracking/workout-tracking-${index + 1}.webp`;
      case "goals":
        return `/client/workout-tracking/workout-tracking-${index + 1}.webp`;
      default:
        return `/new-tab-${index + 1}.webp`;
    }
  };

  // Helper function to safely get image source based on role
  const getImageSrc = (tabValue: string, index: number) => {
    if (currentRole === "client") {
      return getClientImageSrc(tabValue, index);
    } else {
      return getTrainerImageSrc(tabValue, index);
    }
  };



  const getTrainerImageSrc = (tabValue: string, index: number) => {
    // For trainer role, use the old hardcoded tab images
    if (index === 0) return "/tabs.png";
    return `/new-tab-${index + 1}.webp`;
  };

  // Update current tab when role changes
  useEffect(() => {
    if (currentRole === "client") {
      setCurrentTab("coaching");
    } else if (currentRole === "trainer") {
      setCurrentTab("client_management");
    }
  }, [currentRole]);

  // const [windowWidth, setWindowWidth] = React.useState(0);

  console.log('=== ROLE DEBUG ===');
  console.log('Current role:', currentRole);
  console.log('currentRole === "client":', currentRole === "client");
  console.log('tabValuesClient:', tabValuesClient);
  console.log('tabValues:', tabValues);
  const finalValues = currentRole === "client" ? tabValuesClient : tabValues;
  console.log('finalValues:', finalValues);
  console.log('finalValues.length:', finalValues.length);
  console.log('==================');

  console.log('=== TABS MAPPING ===');

  console.log('Mapping tabs for role:', currentRole);
  const tabs = finalValues.map((tabValue, index) => {
    const imagePath = getImageSrc(tabValue, index);
    console.log(`Creating tab ${index}:`, { 
      tabValue, 
      title: t(`${tabValue}.title`),
      imagePath,

      role: currentRole
    });
    return {
      title: t(`${tabValue}.title`),
      value: tabValue,
      content: (
        <div className="w-full overflow-hidden relative font-poppins flex flex-col-reverse md:flex-col gap-10 h-fit rounded-[2rem] p-4 md:px-10 text-xl md:text-4xl text-white bg-gradient-to-t from-primary to-second-primary">
          <div className="flex flex-col gap-10">
            <h2 className="text-xl md:text-5xl font-bold">
              {t(`${tabValue}.title`)}
            </h2>
            <div className="text-lg md:text-2xl">
              <span className="font-bold">
                {t(`${tabValue}.description.first.first_statement`)}
              </span>{" "}
              {t(`${tabValue}.description.first.rest_description`)}
            </div>
          </div>
          <div className="w-full">
            <video
              className="rounded-2xl"
              height={1000}
              autoPlay
              muted
              playsInline
              loop
            >
              <source
                src={index < 3 ? `/tab-${index + 1}.mp4` : "/tab-1.mp4"}
                type="video/mp4"
              />
            </video>
          </div>
        </div>
      ),
    };
  });

  if (!role) return null;

  return (
    <>
      <div className="w-full lg:h-12 h-0 bg-gradient-to-l from-primary to-second-primary "></div>
      <section className="w-full items-center px-4 md:px-12 bg-[#f1eef7]">
        <div className="flex flex-col gap-4 py-5 md:hidden">
          <h2 className={cn(
            "text-4xl md:text-5xl lg:text-6xl xl:text-7xl",
            {
              "leading-none": locale === "en",
              "leading-[1.2] !leading-[1.2]": locale === "ar",
            }
          )}>
            {currentRole === "client" ? t("title_client") : t("title")}
          </h2>
          <p className="text-muted-foreground font-poppins">
            {currentRole === "client" ? t("description_client") : t("description")}
          </p>
        </div>
        {currentRole === "client" ? (
          <Tabs
            value={currentTab}
            onValueChange={setCurrentTab}
            className="flex flex-col-reverse md:flex-row gap-4"
            dir={locale === "ar" ? "rtl" : "ltr"}
          >
            <div className="md:basis-1/2 pb-12 pt-0 lg:pt-12">
              <div className="md:flex flex-col gap-4 hidden">
                <h2 className={cn(
                  "text-4xl md:text-5xl lg:text-6xl xl:text-7xl",
                  {
                    "leading-none": locale === "en",
                    "leading-[1.2] !leading-[1.2]": locale === "ar",
                  }
                )}>
                  {currentRole === "client" ? t("title_client") : t("title")}
                </h2>
                <p className="text-muted-foreground font-poppins">
                  {currentRole === "client" ? t("description_client") : t("description")}
                </p>
              </div>
              <TabsList className="w-full h-fit bg-transparent mt-3 md:mt-10 lg:mt-20 grid grid-cols-2 lg:grid-cols-2 gap-2 lg:gap-4">
                {tabs.map((tab, index) => (
                  <TabsTrigger
                    key={index}
                    value={tab.value}
                    className="text-black flex flex-col items-start gap-2 py-2 lg:py-4 text-lg lg:text-2xl min-h-14 max-w-1/2 text-wrap transition rounded-2xl border-2 border-transparent data-[state=active]:border-primary data-[state=active]:from-primary data-[state=active]:to-second-primary bg-background shadow-lg"
                  >
                    <div className="flex md:justify-start justify-center w-full">
                      {index === 0 && (
                        <TrainerIcon
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 1 && (
                        <NotepadText
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 2 && (
                        <SaladIcon
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 3 && (
                        <ChartColumnIncreasing
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 4 && (
                        <DartIcon
                          size={24}
                          className="text-primary"
                        />
                      )}
                    </div>
                    <h3 className={cn(
                      "xs:text-[14px] w-full md:text-2xl 2xs:text-[13px] text-[11px]",
                      {
                        "text-center md:text-left": locale === "en",
                        "text-center md:text-right leading-[1.2]": locale === "ar",
                      }
                    )}>
                      {tab.title}
                    </h3>
                    <p className={cn(
                      "text-muted-foreground hidden md:block font-poppins text-sm",
                      {
                        "text-center md:text-start": locale === "en",
                        "text-center md:text-end": locale === "ar",
                      }
                    )}>
                      {t(`${tab.value}.description.first.first_statement`)}
                    </p>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="flex-1 h-full self-center">
              {tabs.map((tab, index) => (
                <TabsContent
                  key={index}
                  value={tab.value}
                  className="flex h-full mt-0 w-full max-h-full flex-col  md:justify-center items-center 3xl:items-start"
                >
                  <div className="h-[250px] 2xs:h-[250px] xs:h-[380px] sm:h-[420px] md:h-full flex w-full justify-center items-center">
                    {index === 0 && (role as string) === "trainer" ? (
                      // Client management tab: responsive images
                      <>
                        {/* Small screen image */}
                        <Image
                          src="/tabs.png"
                          alt={tab.value}
                          width={500}
                          height={500}
                          priority
                          className="md:hidden object-contain self-center h-full w-full"
                        />
                        {/* Large screen image */}
                        <Image
                          src="/client-management-prev.webp"
                          alt={tab.value}
                          width={500}
                          height={500}
                          priority
                          className="hidden md:block object-contain self-center h-full 2xl:w-[70%] w-[70%] sm:w-[90%]"
                        />
                      </>
                    ) : (
                      // All other tabs: single image
                      <Image
                        src={getImageSrc(tab.value, index)}
                        alt={tab.value}
                        width={500}
                        height={500}
                        priority
                        className={` ${
                          index === 4
                            ? "w-[84%] md:w-10/12 3xl:w-[760px] object-contain"
                            : index == 1
                            ? "lg:w-6/12 w-[47%] sm:w-[65%] self-center object-contain"
                            : "w-[75%] sm:w-[70%]"
                        }`}
                      />
                    )}
                  </div>
                  <h4 className="pt-6 sm:text-2xl text-[18px] 2xs:text-[21px] xs:text-[22px] md:hidden">
                    {t(`${tab.value}.description.first.first_statement`)}
                  </h4>
                </TabsContent>
              ))}
            </div>
          </Tabs>
        ) : (
          <Tabs
            value={currentTab}
            onValueChange={setCurrentTab}
            className="flex flex-col-reverse md:flex-row gap-4"
            dir={locale === "ar" ? "rtl" : "ltr"}
          >
            <div className="md:basis-1/2 pb-12 pt-0 lg:pt-12">
              <div className="md:flex flex-col gap-4 hidden">
                <h2 className={cn(
                  "text-4xl md:text-5xl lg:text-6xl xl:text-7xl",
                  {
                    "leading-none": locale === "en",
                    "leading-[1.2] !leading-[1.2]": locale === "ar",
                  }
                )}>
                  {t("title")}
                </h2>
                <p className="text-muted-foreground font-poppins">
                  {t("description")}
                </p>
              </div>
              <TabsList className="w-full h-fit bg-transparent mt-3 md:mt-10 lg:mt-20 grid grid-cols-2 lg:grid-cols-2 gap-2 lg:gap-4">
                {tabs.map((tab, index) => (
                  <TabsTrigger
                    key={index}
                    value={tab.value}
                    className="text-black flex flex-col items-start gap-2 py-2 lg:py-4 text-lg lg:text-2xl min-h-14 max-w-1/2 text-wrap transition rounded-2xl border-2 border-transparent data-[state=active]:border-primary data-[state=active]:from-primary data-[state=active]:to-second-primary bg-background shadow-lg"
                  >
                    <div className="flex md:justify-start justify-center w-full">
                      {index === 0 && (
                        <Network
                          size={20}
                          className="text-primary md:text-primary"
                        />
                      )}
                      {index === 1 && (
                        <NotepadText
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 2 && (
                        <ChartColumnIncreasing
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 3 && (
                        <ChartPie
                          size={24}
                          className="text-primary"
                        />
                      )}
                      {index === 4 && (
                        <Handshake
                          size={24}
                          className="text-primary"
                        />
                      )}
                    </div>
                    <h3 className={cn(
                      "xs:text-[14px] w-full md:text-2xl 2xs:text-[13px] text-[11px]",
                      {
                        "text-center md:text-left": locale === "en",
                        "text-center md:text-right leading-[1.2]": locale === "ar",
                      }
                    )}>
                      {tab.title}
                    </h3>
                    <p className={cn(
                      "text-muted-foreground hidden md:block font-poppins text-sm",
                      {
                        "text-center md:text-start": locale === "en",
                        "text-center md:text-end": locale === "ar",
                      }
                    )}>
                      {t(`${tab.value}.description.first.first_statement`)}
                    </p>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="flex-1 h-full self-center">
              {tabs.map((tab, index) => (
                <TabsContent
                  key={index}
                  value={tab.value}
                  className="flex h-full mt-0 w-full max-h-full flex-col  md:justify-center items-center 3xl:items-start"
                >
                  <div className="h-[250px] 2xs:h-[250px] xs:h-[380px] sm:h-[420px] md:h-full flex w-full justify-center items-center">
                    {(() => {
                      const imageSrc = getImageSrc(tab.value, index);
                      console.log(`Rendering image for tab ${index} (${tab.value}):`, imageSrc);
                      return (
                        <Image
                          src={imageSrc}
                          alt={tab.value}
                          width={500}
                          height={500}
                          className={` ${
                            index === 4
                              ? "w-[84%] md:w-10/12 3xl:w-[760px] object-contain"
                              : index == 1
                              ? "lg:w-6/12 w-[47%] sm:w-[65%] self-center object-contain"
                              : "w-[75%] md:w-10/12 3xl:w-[760px] object-contain"
                          }`}
                        />
                      );
                    })()}
                  </div>
                  <h4 className="pt-6 sm:text-2xl text-[18px] 2xs:text-[21px] xs:text-[22px] md:hidden">
                    {t(`${tab.value}.description.first.first_statement`)}
                  </h4>
                </TabsContent>
              ))}
            </div>
          </Tabs>
        )}

        </section>
      <div className="w-full lg:h-12 h-0 bg-gradient-to-l from-primary to-second-primary"></div>
    </>
  );
};

export default TabsSection;
